import React, { useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ScrollShadow } from '@heroui/react'
import { menuGroups } from '../../config/menuConfig.js'
import { cn } from '../../utils/cn.js'

const MobileNav = () => {
    const location = useLocation()
    const scrollRef = useRef(null)

    // Flatten the menu structure
    const allItems = menuGroups.flatMap(group => group.items)

    const isActive = (path) => {
        const currentPath = location.pathname === '/' ? '/dashboard' : location.pathname
        return currentPath === path
    }

    // Auto-scroll to active item
    useEffect(() => {
        const activeElement = document.getElementById(`mobile-nav-${location.pathname}`)
        if (activeElement && scrollRef.current) {
            activeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest', // Since it's vertical scrolling in the page, but horizontal here... 
                // Actually, scrollIntoView works for both axes.
                inline: 'center'
            })
        }
    }, [location.pathname])

    return (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-content1/80 backdrop-blur-md border-t border-default-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] pb-safe pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
            <ScrollShadow
                orientation="horizontal"
                className="w-full h-20 flex items-center gap-4 px-4 hide-scrollbar"
                ref={scrollRef}
            >
                {allItems.map((item, index) => {
                    const Icon = item.icon
                    const active = isActive(item.path)

                    return (
                        <Link
                            key={item.path}
                            id={`mobile-nav-${item.path}`}
                            to={item.path}
                            className="flex flex-col items-center justify-center flex-shrink-0 gap-1.5 min-w-[72px] h-full relative group"
                        >
                            <div className={cn(
                                "p-2.5 rounded-2xl transition-all duration-300 relative overflow-hidden",
                                active
                                    ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 shadow-sm"
                                    : "text-slate-500 dark:text-slate-400 group-active:scale-95"
                            )}>
                                {/* Active Background Glow */}
                                {active && (
                                    <motion.div
                                        layoutId="mobileNavActiveConfig"
                                        className="absolute inset-0 bg-blue-500/10 dark:bg-blue-400/10 rounded-2xl"
                                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                    />
                                )}

                                <Icon className={cn(
                                    "w-6 h-6 relative z-10",
                                    active && "fill-current"
                                )} strokeWidth={active ? 2.5 : 2} />
                            </div>

                            <span className={cn(
                                "text-[10px] font-bold tracking-wide transition-colors",
                                active ? "text-blue-600 dark:text-blue-400" : "text-slate-500 dark:text-slate-500"
                            )}>
                                {item.title}
                            </span>


                        </Link>
                    )
                })}
            </ScrollShadow>
        </div>
    )
}

export default MobileNav
